import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core'
import { OrdersService, OrdersTableColumns } from './orders.service'
import { MatPaginator } from '@angular/material/paginator'
import { MatTable, MatTableDataSource } from '@angular/material/table'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { OrderNotificationService } from './order-notification-service/order-notification.service'

/*
For this piece of code it just reassings the id of every order to its index in the orders array
and sets the last index in the array in the array to be easily added in the app and assigned
to every new order and updated in the app and in the database

for(let i of this.ordersService.allOrders.keys()){
  this.ordersService.allOrders[i].humId = i
  if(i+1 === this.ordersService.allOrders.length){
    this.ordersService.lastIndex = i;
    this.ordersService.addLastIndex(this.ordersService.lastIndex);
  }
}
  */
import { forkJoin } from 'rxjs'

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})
export class OrdersComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatTable) table!: MatTable<OrdersTableColumns>
  @ViewChild(MatSort, { static: true }) matSort!: MatSort

  public Math = Math;

  displayedOrders: any[] = [];

  // Dropdown states
  showTypeDropdown = false;
  showSortDropdown = false;

  // Delete modal state
  showDeleteModal = false;
  private orderToDelete: { orderId: string; userId?: string; type?: string; displayId: any } | null = null;

  public ordersColumns: string[] = [ 'id', 'date', 'user_data', 'type', 'status', 'total', 'action' ]
  constructor(
    private route: ActivatedRoute,
    public ordersService: OrdersService,
    public OrderNotification: OrderNotificationService,
    public builderService: BuilderService,
    private router: Router,
  ) {}

  /**
   * UI summary cards displayed above the orders table.
   * Each card represents a different type of order.
   */
  orderSummary = [
    {
      title: 'Mobile App',
      type: 'app',
      count: 0,
      icon: 'mobile_gray.svg',
      iconClass: 'app'
    },
    {
      title: 'Website',
      type: 'website',
      count: 0,
      icon: 'website_gray.svg',
      iconClass: 'website'
    },
    {
      title: 'POS System',
      type: 'pos',
      count: 0,
      icon: 'cart_gray.svg',
      iconClass: 'pos'
    },
    {
      title: 'QR Menu',
      type: 'menu',
      count: 0,
      icon: 'qr_gray.svg',
      iconClass: 'qr'
    },
    {
      title: 'Social Agent',
      type: 'social_agent',
      count: 0,
      icon: 'chat_gray.svg',
      iconClass: 'social'
    }
  ];

  ngOnInit(): void {
    this.getAllData();
  }
  ngAfterViewInit(): void {
    this.ordersService.ordersDataSource.paginator = this.paginator
    this.ordersService.ordersDataSource.sort = this.matSort

    // Close dropdowns when clicking outside
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown')) {
        this.showTypeDropdown = false;
        this.showSortDropdown = false;
      }
    });
  }

  toggleTypeDropdown(event: Event): void {
    event.stopPropagation();
    this.showTypeDropdown = !this.showTypeDropdown;
    this.showSortDropdown = false;
  }

  toggleSortDropdown(event: Event): void {
    event.stopPropagation();
    this.showSortDropdown = !this.showSortDropdown;
    this.showTypeDropdown = false;
  }

  selectType(type: string): void {
    this.ordersService.selectedType = type;
    this.filterByType();
    this.showTypeDropdown = false;
  }

  selectSort(sort: string): void {
    this.ordersService.selectedSort = sort;
    this.sortOrders();
    this.showSortDropdown = false;
  }

  getSortLabel(): string {
    const labels: { [key: string]: string } = {
      'id_asc': 'ID — ASC',
      'id_desc': 'ID — DESC',
      'date_new': 'Date — Newest',
      'date_old': 'Date — Oldest',
      'total_high': 'Total — High → Low',
      'total_low': 'Total — Low → High'
    };
    return labels[this.ordersService.selectedSort] || 'Sort By';
  }

  /** Filters displayed orders by selected type — resets to page 1 and fetches from Supabase */
  filterByType(): void {
    this.ordersService.pageIndex = 0
    this.loadPage(0)
  }

  /** Updates displayed orders from the current datasource (already the fetched page) */
  updateDisplayedOrders(): void {
    this.displayedOrders = this.ordersService.ordersDataSource.data
  }

  /** Fetches count + one page from Supabase and updates the table */
  private loadPage(page: number): void {
    const type = this.ordersService.selectedType
    const sort = this.ordersService.selectedSort
    this.builderService.showUpdateProgress = true
    this.ordersService.fetchCount(type).subscribe(count => {
      this.ordersService.paginationCount = count
    })
    this.ordersService.fetchPage(page, this.ordersService.pageSize, type, sort).subscribe({
      next: orders => {
        this.ordersService.ordersDataSource.data = orders
        this.displayedOrders = orders
        this.builderService.showUpdateProgress = false
      },
      error: err => {
        console.error('[loadPage] fetch failed:', err)
        this.builderService.showUpdateProgress = false
      }
    })
  }

  /** Pagination navigation */
  previousPage(): void {
    if (this.ordersService.pageIndex > 0) {
      this.ordersService.pageIndex--
      this.loadPage(this.ordersService.pageIndex)
    }
  }

  nextPage(): void {
    const totalPages = this.getTotalPages()
    if (this.ordersService.pageIndex < totalPages - 1) {
      this.ordersService.pageIndex++
      this.loadPage(this.ordersService.pageIndex)
    }
  }

  getTotalPages(): number {
    return Math.ceil(this.ordersService.paginationCount / this.ordersService.pageSize) || 1
  }

  changePageSize(size: number): void {
    this.ordersService.pageSize = Number(size)
    this.ordersService.pageIndex = 0
    this.loadPage(0)
  }

  /** Sorts orders — reloads page 1 from Supabase with the selected sort order */
  sortOrders(): void {
    this.ordersService.pageIndex = 0
    this.loadPage(0)
  }

  /** Converts DD-MM-YYYY string into a timestamp number */
  parseDate(dateString: string): number {
    if (!dateString) return 0

    const [day, month, year] = dateString.split('-')
    return new Date(+year, +month - 1, +day).getTime()
  }
  /** Updates the summary card counts (total orders) for each order type */
  updateOrderSummaryCounts(): void {
    forkJoin(this.orderSummary.map(card => this.ordersService.fetchCount(card.type))).subscribe(counts => {
      this.orderSummary = this.orderSummary.map((card, i) => ({ ...card, count: counts[i] || 0 }))
    })
  }

  toggleMute(checked: boolean) {
    this.OrderNotification.setMuteSound(checked)
  }
  getAllData = () => {
    this.updateOrderSummaryCounts()
    // Server-side paginated load for the table display
    this.ordersService.pageIndex = 0
    this.loadPage(0)
  }

  /** Converts a date string (DD-MM-YYYY) to a structured date object */
  parseDateToObject(dateString: string): { day: number; month: number; year: number } {
    if (!dateString) return { day: 0, month: 0, year: 0 }
    const [day, month, year] = dateString.split('-')
    return {
      day: +day,
      month: +month - 1, // JavaScript months are 0-indexed
      year: +year
    }
  }

  editOrder = (id: string, type?: string) => {
    this.router.navigate([`/orders/single-order/${id}`], { relativeTo: this.route })
  }

  openDeleteModal(order: any): void {
    this.orderToDelete = { orderId: order.order_id, userId: order.user, type: order.type, displayId: order.humId }
    this.showDeleteModal = true
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false
    this.orderToDelete = null
  }

  get orderToDeleteDisplayId(): any {
    return this.orderToDelete?.displayId ?? ''
  }

  confirmDeleteOrder(): void {
    if (!this.orderToDelete) return
    const { orderId, userId, type } = this.orderToDelete
    this.ordersService.deleteOrder(orderId, userId, type).subscribe({
      next: () => {
        this.updateOrderSummaryCounts()
        this.closeDeleteModal()
        // Refresh count and reload current page from Supabase
        this.ordersService.fetchCount(this.ordersService.selectedType).subscribe((count: number) => {
          this.ordersService.paginationCount = count
          const maxPage = Math.max(0, Math.ceil(count / this.ordersService.pageSize) - 1)
          if (this.ordersService.pageIndex > maxPage) {
            this.ordersService.pageIndex = maxPage
          }
          this.loadPage(this.ordersService.pageIndex)
        })
      },
      error: (err: any) => {
        console.log(`Failed to delete order ${orderId}:`, err)
        this.closeDeleteModal()
      }
    })
  }

  exportToExcel = () => {
    this.builderService.showUpdateProgress = true
    this.ordersService.getOrders().subscribe({
      next: (orders: any[]) => {
        this.builderService.showUpdateProgress = false
        if (orders.length > 0) {
          this.ordersService.exportOrdersToExcel(orders)
        } else {
          alert('No orders to export')
        }
      },
      error: () => {
        this.builderService.showUpdateProgress = false
        alert('Failed to load orders for export.')
      }
    })
  }
  /** Extracts "MM-YYYY" from a date string formatted as "DD-MM-YYYY" */
  getMonth(dateString: string): string {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('-');
    return `${month}-${year}`; // Example: 02-2025
  }
}
